import { Button, TextButton } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Modal } from '@creditornot/cbt-modal';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';
import Loading from 'core/Loading';
import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';
import { ItemDiscountFormValues } from 'modules/campaigns/types';

import { DiscountedItemsModalContent } from './DiscountedItemsModalContent';

type DiscountedItemsModalProps = {
  menuItems: DiscountedItem[];
  isLoadingMenuItems: boolean;
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: () => void;
  onCancel: () => void;
};

const MODAL_SIZE_BREAKPOINT = 550;

const Footer = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  gap: 16px;

  @media (width < ${MODAL_SIZE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const FooterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (width < ${MODAL_SIZE_BREAKPOINT}px) {
    justify-content: space-between;
  }
`;

const StyledButton = styled(Button)`
  @media (width < ${MODAL_SIZE_BREAKPOINT}px) {
    flex: 1 1 50%;
  }
`;

const SelectedItemsCount = styled.p`
  ${typographyCss.Body3}
  color: ${cbSemanticCssVars.textSecondary};
  margin: 0;
`;

const EmphasizedCount = styled.span`
  font-weight: 700;
`;

export const DiscountedItemsModal = ({
  menuItems,
  isLoadingMenuItems,
  isOpen,
  onDismiss,
  onSubmit,
  onCancel,
}: DiscountedItemsModalProps) => {
  const { formatMessage } = useIntl();

  return (
    <Modal
      open={isOpen}
      onDismiss={onDismiss}
      title={formatMessage({ id: 'campaigns.form.item.select-items-or-categories' })}
      sizeConfig={{
        desktop: {
          type: 'centered',
          width: '100%',
          maxWidth: '800px',
          height: '100%',
          maxHeight: '700px',
        },
        mobile: { type: 'fullScreen' },
      }}
      disableContentPadding
      footer={
        <Field<ItemDiscountFormValues['itemIds']> name="itemIds">
          {({ input }) => (
            <Footer>
              <FooterGroup>
                {!!input.value.length ? (
                  <>
                    <TextButton onClick={() => input.onChange([])}>
                      <FormattedMessage id="campaigns.form.venues.deselect-all" />
                    </TextButton>
                    <SelectedItemsCount>
                      <FormattedMessage
                        id="campaigns.form.item.selected-items-count"
                        values={{
                          selectedItems: <EmphasizedCount>{input.value.length}</EmphasizedCount>,
                          totalItems: menuItems.length,
                        }}
                      />
                    </SelectedItemsCount>
                  </>
                ) : (
                  <TextButton onClick={() => input.onChange(menuItems.map(item => item.id))}>
                    <FormattedMessage id="campaigns.form.venues.select-all" />
                  </TextButton>
                )}
              </FooterGroup>
              <FooterGroup>
                <StyledButton variant="lightBlue" size="small" onClick={onCancel}>
                  <FormattedMessage id="state.cancel" />
                </StyledButton>
                <StyledButton
                  type="submit"
                  variant="blue"
                  size="small"
                  disabled={!input.value.length}
                  onClick={onSubmit}
                >
                  <FormattedMessage id="campaigns.form.item.select-items" />
                </StyledButton>
              </FooterGroup>
            </Footer>
          )}
        </Field>
      }
    >
      {isLoadingMenuItems ? (
        <Loading />
      ) : (
        <Field<ItemDiscountFormValues['itemIds']> name="itemIds">
          {({ input }) => (
            <DiscountedItemsModalContent
              menuItems={menuItems}
              selectedItemIds={input.value}
              onSelectionChange={input.onChange}
            />
          )}
        </Field>
      )}
    </Modal>
  );
};
